/* eslint-disable no-underscore-dangle */

import * as React from "react";
import { useCallback, useEffect, useState } from "react";

import { graphql } from "gatsby";

import RichText from "cms/components/RichText";

import Badge from "cms/components/Badge";
import Button from "cms/components/Button";
import Logo from "cms/components/Logo";
import {
  App,
  Banner,
  Container,
  Content,
  Controls,
  ControlsSignup,
  GhostButton,
  Link,
  LogoContainer,
  Main,
  Menu,
  MenuItem,
  MenuItemLink,
  NavigationTypeFormButton,
  SubMenu,
  SubMenuItem,
  SubMenuItemDescription,
  SubMenuItemLabel,
  SubMenuItemLink,
  ToggleIcon,
  Toggle,
  AppSignup,
  MenuItemToggle,
  MenuItemToggleIcon,
  MenuItemText,
  SubMenuItemText,
  MenuItemIcon,
  BannerMagicIcon,
} from "./Navigation.styles";
import { throttle } from "lodash";

const MenuItemComponent: React.FC<{
  menuItem: Queries.Maybe<Queries.SanityMenuItem>;
}> = ({ menuItem }) => {
  const [isOpen, setOpen] = useState(false);

  const onToggle = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
    setOpen((isOpen) => !isOpen);
  }, []);

  if (!menuItem) return null;

  const hasSubMenu = (menuItem.submenuItems?.length ?? 0) > 0;

  return (
    <MenuItem key={menuItem._key}>
      <MenuItemLink href={menuItem.url ?? undefined} $withSubMenu={hasSubMenu}>
        <MenuItemText
          $variant={menuItem.variant === "magic" ? "magic" : "regular"}
        >
          {menuItem.variant === "magic" && <MenuItemIcon />}

          {menuItem.text}
        </MenuItemText>

        {hasSubMenu && (
          <MenuItemToggle onClick={onToggle}>
            <MenuItemToggleIcon $isOpen={isOpen} />
          </MenuItemToggle>
        )}
      </MenuItemLink>

      {hasSubMenu && (
        <SubMenu $isOpen={isOpen}>
          {menuItem.submenuItems?.map((submenuItem) => {
            if (!submenuItem) return null;

            return (
              <SubMenuItem key={submenuItem._key}>
                <SubMenuItemLink href={submenuItem.url ?? undefined}>
                  <SubMenuItemLabel>
                    <SubMenuItemText
                      $variant={
                        submenuItem.variant === "magic" ? "magic" : "regular"
                      }
                    >
                      {submenuItem.text}
                    </SubMenuItemText>

                    {submenuItem.badge && (
                      <Badge
                        variant={
                          submenuItem.variant === "magic" ? "magic" : "primary"
                        }
                        text={submenuItem.badge}
                      />
                    )}
                  </SubMenuItemLabel>
                  <SubMenuItemDescription>
                    {submenuItem.description}
                  </SubMenuItemDescription>
                </SubMenuItemLink>
              </SubMenuItem>
            );
          })}
        </SubMenu>
      )}
    </MenuItem>
  );
};

export interface NavigationProps extends Queries.SanityNavigation {}

const Navigation: React.FC<NavigationProps> = ({
  showBanner,
  _rawBannerText,
  bannerVariant,
  menuItems,
}) => {
  const [isOpen, setOpen] = useState(false);
  const [isScrolling, setScrolling] = useState(false);

  const pathname =
    typeof window !== "undefined" ? window.location.pathname : "";
  const [currentUrl, setCurrentUrl] = useState("");
  const onToggle = () => setOpen(!isOpen);

  useEffect(() => {
    setCurrentUrl(pathname.slice(0, -1));
  }, [currentUrl, pathname, menuItems]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isOpen]);

  useEffect(() => {
    const onScroll = throttle(
      () => {
        if (window.scrollY > 0) {
          setScrolling(true);
        } else {
          setScrolling(false);
        }
      },
      250,
      { leading: true, trailing: true },
    );

    onScroll();
    window.addEventListener("scroll", onScroll);

    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  return (
    <Container $isScrolling={isScrolling}>
      {showBanner && bannerVariant && (
        <Banner $variant={bannerVariant}>
          {bannerVariant === "magic" && <BannerMagicIcon />}

          <RichText noStyle text={_rawBannerText} />
        </Banner>
      )}
      <Content $isOpen={isOpen}>
        <LogoContainer>
          <Link href="/">
            <Logo />
          </Link>
        </LogoContainer>

        <Main $isOpen={isOpen} $withBanner={!!showBanner}>
          <Menu>
            {menuItems?.map((menuItem) => {
              if (!menuItem) return null;

              return (
                <MenuItemComponent key={menuItem._key} menuItem={menuItem} />
              );
            })}
          </Menu>

          <App>
            <GhostButton href="/pricing/">Pricing</GhostButton>
            <NavigationTypeFormButton id="WccWfRLY">
              Contact sales
            </NavigationTypeFormButton>
            <GhostButton href="https://app.uizard.io/login">Log in</GhostButton>
            <AppSignup>
              <Link href="https://app.uizard.io/sign-up/">
                <Button
                  label="Sign up for free"
                  variant="primary"
                  size="small"
                />
              </Link>
            </AppSignup>
          </App>
        </Main>

        <Controls>
          <ControlsSignup>
            <Link href="https://app.uizard.io/sign-up/">
              <Button label="Sign up for free" variant="primary" size="small" />
            </Link>
          </ControlsSignup>

          <Toggle onClick={onToggle}>
            <ToggleIcon $isOpen={isOpen} />
          </Toggle>
        </Controls>
      </Content>
    </Container>
  );
};

export const query = graphql`
  fragment Navigation on SanityNavigation {
    _key
    showBanner
    _rawBannerText(resolveReferences: { maxDepth: 5 })
    bannerVariant
    menuItems {
      _key
      text
      url
      variant

      submenuItems {
        _key
        text
        url
        description
        badge
        variant
      }
    }
  }
`;

export default Navigation;
